import { useCallback } from 'react';
import { getUsersByIds, User } from '../api/user';
import { getPermissionsBatchByResourceIDs, ResourceType, GranteeType } from '../api/userPermissions';
import { useGlobalUsers } from '../contexts/GlobalUsersContext';
import { GlobalUser } from '../contexts/GlobalUsersContext';

const USER_QUERY_BATCH_DELAY_MS = 20;
const USER_QUERY_BATCH_SIZE = 100;
const USER_QUERY_FAILURE_COOLDOWN_MS = 30_000;

interface PendingUserQuery {
  generation: number;
  promise: Promise<User | undefined>;
  resolve: (user: User | undefined) => void;
}

// 所有 useBatchUsersQuery 实例共用同一个查询协调器，避免多个 Context
// 在全局缓存写入前对同一用户发起重复请求。
const pendingUserQueries = new Map<string, PendingUserQuery>();
const queuedUserIds = new Set<string>();
const failedUserQueryUntil = new Map<string, number>();
let userQueryFlushTimer: ReturnType<typeof setTimeout> | null = null;
let userQueryGeneration = 0;

const flushUserQueryQueue = async (): Promise<void> => {
  userQueryFlushTimer = null;
  const flushGeneration = userQueryGeneration;
  const userIds = Array.from(queuedUserIds);
  queuedUserIds.clear();

  for (let start = 0; start < userIds.length; start += USER_QUERY_BATCH_SIZE) {
    const chunk = userIds.slice(start, start + USER_QUERY_BATCH_SIZE);
    try {
      const response = await getUsersByIds(chunk);
      if (flushGeneration !== userQueryGeneration) continue;
      const usersById = new Map((response.data || []).map(user => [user.userID, user]));

      chunk.forEach(userId => {
        const user = usersById.get(userId);
        pendingUserQueries.get(userId)?.resolve(user);
        if (user) {
          failedUserQueryUntil.delete(userId);
        } else {
          // 用户不存在或当前账号不可见时，避免每次缓存写入都重复查询。
          failedUserQueryUntil.set(userId, Date.now() + USER_QUERY_FAILURE_COOLDOWN_MS);
        }
      });
    } catch (error) {
      if (flushGeneration !== userQueryGeneration) continue;
      const retryAfter = Date.now() + USER_QUERY_FAILURE_COOLDOWN_MS;
      chunk.forEach(userId => {
        failedUserQueryUntil.set(userId, retryAfter);
        pendingUserQueries.get(userId)?.resolve(undefined);
      });
      console.warn(`批量查询用户信息失败，${USER_QUERY_FAILURE_COOLDOWN_MS / 1000} 秒内不再重试（不影响主流程）:`, error);
    } finally {
      chunk.forEach(userId => {
        if (pendingUserQueries.get(userId)?.generation === flushGeneration) {
          pendingUserQueries.delete(userId);
        }
      });
    }
  }
};

const scheduleUserQueryFlush = (): void => {
  if (userQueryFlushTimer) return;
  userQueryFlushTimer = setTimeout(() => {
    void flushUserQueryQueue();
  }, USER_QUERY_BATCH_DELAY_MS);
};

const enqueueUserQueries = (userIds: string[]): Promise<User[]> => {
  const now = Date.now();
  const promises = Array.from(new Set(userIds)).flatMap(userId => {
    if (!userId) return [];

    const retryAfter = failedUserQueryUntil.get(userId);
    if (retryAfter && retryAfter > now) return [];
    if (retryAfter) failedUserQueryUntil.delete(userId);

    const existing = pendingUserQueries.get(userId);
    if (existing) return [existing.promise];

    let resolveQuery!: (user: User | undefined) => void;
    const promise = new Promise<User | undefined>(resolve => {
      resolveQuery = resolve;
    });
    pendingUserQueries.set(userId, { generation: userQueryGeneration, promise, resolve: resolveQuery });
    queuedUserIds.add(userId);
    return [promise];
  });

  if (queuedUserIds.size > 0) scheduleUserQueryFlush();

  return Promise.all(promises).then(users => users.filter((user): user is User => Boolean(user)));
};

/** 登出时重置查询代次，防止旧账号的延迟响应回填全局用户缓存。 */
export const resetBatchUsersQueryCoordinator = (): void => {
  userQueryGeneration += 1;
  if (userQueryFlushTimer) {
    clearTimeout(userQueryFlushTimer);
    userQueryFlushTimer = null;
  }
  queuedUserIds.clear();
  pendingUserQueries.forEach(query => query.resolve(undefined));
  pendingUserQueries.clear();
  failedUserQueryUntil.clear();
};

/**
 * 批量查询用户信息的 Hook
 * 自动过滤已缓存的用户ID，只查询未缓存的用户
 */
export const useBatchUsersQuery = () => {
  const { hasUser, addUsers } = useGlobalUsers();

  /**
   * 批量查询用户信息并存入 GlobalUsersContext
   * @param userIds 用户ID数组
   * @returns Promise<void>
   */
  const queryUsers = useCallback(async (userIds: string[]): Promise<void> => {
    if (userIds.length === 0) {
      return;
    }

    // 过滤已缓存的用户ID
    const uncachedIds = userIds.filter(id => id && !hasUser(id));

    if (uncachedIds.length === 0) {
      return;
    }

    try {
      const users = await enqueueUserQueries(uncachedIds);
      if (users.length > 0) {
        // 转换为 GlobalUser 格式
        const globalUsers: GlobalUser[] = users.map(u => ({
          userID: u.userID,
          userName: u.userName,
          userPhoneCountryCode: u.userPhoneCountryCode,
          userPhone: u.userPhone,
          userEmail: u.userEmail,
          userRealName: u.userRealName,
          userNickName: u.userNickName,
          userAvatar: u.userAvatar,
          userPosition: u.userPosition,
          userRole: u.userRole,
          userCountry: u.userCountry,
          userCity: u.userCity,
          userAccountType: u.userAccountType,
          userAccountStatus: u.userAccountStatus,
          userSubscriptionBreakdownVersion: u.userSubscriptionBreakdownVersion,
          userSubscriptionBreakdownStartDate: u.userSubscriptionBreakdownStartDate,
          userSubscriptionBreakdownExpirationDate: u.userSubscriptionBreakdownExpirationDate,
          userSubscriptionBudgetsVersion: u.userSubscriptionBudgetsVersion,
          userSubscriptionBudgetsStartDate: u.userSubscriptionBudgetsStartDate,
          userSubscriptionBudgetsExpirationDate: u.userSubscriptionBudgetsExpirationDate,
          userOrgID: u.userOrgID,
          createdAt: u.createdAt,
          updatedAt: u.updatedAt,
        }));
        
        // 存入 GlobalUsersContext（自动排重）
        addUsers(globalUsers);
      }
    } catch (error) {
      console.warn('批量查询用户信息失败（不影响主流程）:', error);
    }
  }, [hasUser, addUsers]);

  /**
   * 根据资源类型收集 OwnerID（自动过滤已缓存的用户ID）
   * @param data 数据对象或数组
   * @param resourceType 资源类型
   * @param filterCached 是否过滤已缓存的用户ID，默认为 true
   * @returns 用户ID数组
   */
  const collectOwnerIDs = useCallback((data: any, resourceType: string, filterCached: boolean = true): string[] => {
    const userIds = new Set<string>();

    const collectFromObject = (obj: any) => {
      if (!obj || typeof obj !== 'object') {
        return;
      }

      // 根据资源类型收集对应的 OwnerID
      switch (resourceType) {
        case 'Film':
          if (obj.filmOwnerID) {
            if (!filterCached || !hasUser(obj.filmOwnerID)) {
              userIds.add(obj.filmOwnerID);
            }
          }
          break;
        case 'Budget':
          if (obj.budgetOwnerID) {
            if (!filterCached || !hasUser(obj.budgetOwnerID)) {
              userIds.add(obj.budgetOwnerID);
            }
          }
          if (Array.isArray(obj.budgetItems)) {
            const collectBudgetItemOwners = (items: any[]) => items.forEach(item => {
              if (item?.budgetItemOwnerID && (!filterCached || !hasUser(item.budgetItemOwnerID))) {
                userIds.add(item.budgetItemOwnerID);
              }
              if (Array.isArray(item?.budgetItemChildren)) collectBudgetItemOwners(item.budgetItemChildren);
            });
            collectBudgetItemOwners(obj.budgetItems);
          }
          break;
        case 'BudgetItem':
          if (obj.budgetItemOwnerID) {
            if (!filterCached || !hasUser(obj.budgetItemOwnerID)) {
              userIds.add(obj.budgetItemOwnerID);
            }
          }
          break;
        case 'Breakdown':
          if (obj.breakdownOwnerID) {
            if (!filterCached || !hasUser(obj.breakdownOwnerID)) {
              userIds.add(obj.breakdownOwnerID);
            }
          }
          break;
        case 'Scene':
          if (obj.sceneCreatorID) {
            if (!filterCached || !hasUser(obj.sceneCreatorID)) {
              userIds.add(obj.sceneCreatorID);
            }
          }
          if (obj.sceneInfo?.sceneCreatorID) {
            const creatorId = obj.sceneInfo.sceneCreatorID;
            if (!filterCached || !hasUser(creatorId)) {
              userIds.add(creatorId);
            }
          }
          break;
        case 'Schedule':
          if (obj.scheduleOwnerID) {
            if (!filterCached || !hasUser(obj.scheduleOwnerID)) {
              userIds.add(obj.scheduleOwnerID);
            }
          }
          if (obj.schedules && Array.isArray(obj.schedules)) {
            obj.schedules.forEach((item: unknown) => collectFromObject(item));
          }
          break;
      }

      // 递归处理子项（如预算项的子项）
      if (obj.budgetItemChildren && Array.isArray(obj.budgetItemChildren)) {
        obj.budgetItemChildren.forEach((child: any) => collectFromObject(child));
      }
    };

    if (Array.isArray(data)) {
      data.forEach(item => collectFromObject(item));
    } else {
      collectFromObject(data);
    }

    return Array.from(userIds);
  }, [hasUser]);

  /**
   * 查询权限表获取 granteeID（granteeType 为"用户"），自动过滤已缓存的用户ID
   * @param resourceType 资源类型
   * @param resourceIDs 资源ID数组
   * @param filterCached 是否过滤已缓存的用户ID，默认为 true
   * @returns 用户ID数组
   */
  const collectPermissionUserIDs = useCallback(async (
    resourceType: ResourceType,
    resourceIDs: string[],
    filterCached: boolean = true
  ): Promise<string[]> => {
    if (resourceIDs.length === 0) {
      return [];
    }

    try {
      const permissionsMap = await getPermissionsBatchByResourceIDs(resourceType, resourceIDs);
      const userIds = new Set<string>();

      permissionsMap.forEach((permissions) => {
        permissions.forEach((permission) => {
          // 只收集 granteeType 为"用户"的 granteeID
          if (permission.granteeType === GranteeType.USER && permission.granteeID) {
            // 如果启用过滤，只添加未缓存的用户ID
            if (!filterCached || !hasUser(permission.granteeID)) {
              userIds.add(permission.granteeID);
            }
          }
        });
      });

      return Array.from(userIds);
    } catch (error) {
      console.warn(`查询权限获取用户ID失败（不影响主流程）:`, error);
      return [];
    }
  }, [hasUser]);

  /**
   * 收集用户ID并批量查询（组合 OwnerID 和权限用户ID）
   * 自动过滤 GlobalUsersContext 中已缓存的用户ID，只查询未缓存的用户
   * @param data 数据对象或数组
   * @param resourceType 资源类型
   * @param resourceIDs 资源ID数组（用于权限查询）
   * @returns Promise<void>
   */
  const collectAndQueryUsers = useCallback(async (
    data: any,
    resourceType: string,
    resourceIDs: string[] = []
  ): Promise<void> => {
    const userIds = new Set<string>();

    // 1. 收集 OwnerID（自动过滤已缓存的用户ID）
    const ownerIDs = collectOwnerIDs(data, resourceType, true);
    ownerIDs.forEach(id => userIds.add(id));

    // 2. 收集权限相关的用户ID（自动过滤已缓存的用户ID）
    if (resourceIDs.length > 0) {
      const resourceTypeEnum = getResourceTypeEnum(resourceType);
      if (resourceTypeEnum) {
        const permissionUserIDs = await collectPermissionUserIDs(resourceTypeEnum, resourceIDs, true);
        permissionUserIDs.forEach(id => userIds.add(id));
      }
    }

    // 3. 批量查询用户信息（queryUsers 内部会再次过滤，确保只查询未缓存的用户）
    if (userIds.size > 0) {
      await queryUsers(Array.from(userIds));
    }
  }, [collectOwnerIDs, collectPermissionUserIDs, queryUsers]);

  return {
    queryUsers,
    collectOwnerIDs,
    collectPermissionUserIDs,
    collectAndQueryUsers,
  };
};

/**
 * 将资源类型字符串转换为 ResourceType 枚举
 */
function getResourceTypeEnum(resourceType: string): ResourceType | null {
  switch (resourceType) {
    case 'Film':
      return ResourceType.FILM;
    case 'Budget':
      return ResourceType.BUDGET;
    case 'BudgetItem':
      return ResourceType.BUDGET_ITEM;
    case 'Breakdown':
      return ResourceType.BREAKDOWN;
    case 'Scene':
      return ResourceType.SCENE;
    case 'Schedule':
      return ResourceType.SCHEDULE;
    case 'Slot':
      return ResourceType.SLOT;
    default:
      return null;
  }
}
